import { Box, Flex, Text } from '@chakra-ui/react';
import { Banner, Button, TextArea, useToast } from '@morressier/alembic';
import { IDecisionModalProps } from './decisionModalHelper';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useRevertDecision } from 'src/api/commands';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import { useSteps } from './hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  emailContentSchema,
  revertDecisionValidator
} from '@publishing-engine/domain/validation';
import { FormProvider, useForm } from 'react-hook-form';
import ComposeDecisionEmail, {
  EmailContentForm,
  EmailContentFormDefaultValues
} from './ComposeDecisionEmail';
import StepHeader from '../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { Overlay } from '../../Overlay';
import { buildEmailPayload } from '../../../utils/normalizeEmailRecipients';
import { SUBMISSION_STATUS_MAP } from '../../../constants';
import { useAttachmentSync } from '../../../hooks/useAttachmentSync';
import { ISubmissionWorkflowState } from '@publishing-engine/domain/models';
import { FieldLabel } from '../../Field';
import { IRevertDecisionCommandData } from '@publishing-engine/domain/actions/v1/submissionWorkflow/revertDecision';
import { useCommandEmailTemplates } from '../../../hooks/useCommandEmailTemplates';
import DecisionLayout from './DecisionLayout';
import { useEffect } from 'react';

export const EditorialRevertDecisionModal = createEditorialModal(
  ({ submissionId, submissionContextId, onClose }: IDecisionModalProps) => {
    const toast = useToast();

    const {
      refetchSubmission,
      workflowResponse: {
        workflowData: { currentState, previousState }
      },
      organizationId,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName: 'RevertDecision'
    });

    const { currentStep, totalSteps, goToNextStep, goToPreviousStep } =
      useSteps(hasCustomizableTemplates ? 2 : 0);

    const previousPhase =
      SUBMISSION_STATUS_MAP[previousState as ISubmissionWorkflowState]?.label ??
      previousState;
    const currentPhase =
      SUBMISSION_STATUS_MAP[currentState as ISubmissionWorkflowState]?.label ??
      currentState;

    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: EmailContentFormDefaultValues,
      resolver: zodResolver(emailContentSchema),
      mode: 'onChange'
    });
    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);
    const revertDecisionMutation = useRevertDecision(
      submissionId,
      submissionContextId,
      refetchSubmission,
      organizationId,
      buildEmailPayload(emailContent)
    );
    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const methods = useForm<IRevertDecisionCommandData>({
      defaultValues: {},
      resolver: zodResolver(revertDecisionValidator)
    });

    const {
      handleSubmit,
      getValues,
      register,
      watch,
      formState: { errors }
    } = methods;

    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const handleConfirmDecision = async () => {
      const values = getValues();
      const trimmedValues = {
        ...values,
        reason: values.reason?.trim()
      };

      const res = await revertDecisionMutation.mutateAsync(trimmedValues);
      if (!res.isSuccess) {
        return toast({
          variant: 'error',
          title: 'Error Reverting Decision',
          description: `There was an error while reverting submission to ${previousPhase}.`
        });
      }
      await submitEditorNote(editorNote);
      localStorage.removeItem(`${submissionId}.revisionRequest.editorComment`);

      toast({
        title: 'Decision Reverted',
        description: `The workflow phase has reverted to ${previousPhase}. More details in the Activity tab.`,
        variant: 'success',
        isClosable: true
      });
      onClose();
    };
    const onSubmitFirstStep = () => {
      if (hasCustomizableTemplates) {
        goToNextStep();
      } else {
        handleConfirmDecision();
      }
    };

    const renderFirstStep = () => (
      <form onSubmit={handleSubmit(onSubmitFirstStep)}>
        <Box marginX="auto">
          <StepHeader
            currentStep={currentStep}
            totalSteps={totalSteps}
            heading="Revert Decision?"
            description={`You are about to revert this submission from ${currentPhase} to ${previousPhase}.`}
            marginY={2}
          />
        </Box>
        <Flex flexDirection="column" gap={8} padding={8} paddingTop={3}>
          <Banner
            content={
              <Flex flexDirection={'column'} gap={2}>
                <Text>
                  Reverting this decision will only change the workflow phase of
                  this submission. All other updates remain intact:
                </Text>
                <Text marginStart={1}>
                  <ul>
                    <li>
                      All edits to submission data (metadata, files, etc.);
                    </li>
                    <li>Activity logs and notes.</li>
                  </ul>
                </Text>
              </Flex>
            }
            heading="Decision Reversal Effects"
            variant="attention"
          />

          <Flex gap={2} flexDirection="column">
            <FieldLabel
              isRequired
              label="Reason for Reversal"
              subText="Provide a reversal reason to document this action in the submission's Activity log."
            />
            <TextArea
              value={watch('reason')}
              {...register('reason', {
                required: 'Reason is required',
                validate: value =>
                  value.trim().length > 0 ||
                  'Reason cannot contain only whitespace'
              })}
              placeholder="Enter a reason for reversal"
              isInvalid={Boolean(errors.reason)}
              errorMessage={errors?.reason?.message}
              rows={4}
            />
          </Flex>

          <EditorialNote value={editorNote} onChange={setEditorNote} />
        </Flex>
      </form>
    );
    const renderEmailStep = () => (
      <FormProvider {...emailContentMethods}>
        <form
          onSubmit={emailContentMethods.handleSubmit(handleConfirmDecision)}
        >
          <Box marginX="auto" py={8} padding={4}>
            <StepHeader
              currentStep={currentStep}
              totalSteps={totalSteps}
              heading="Send Email Notification"
              description="Compose an email to notify the author about the decision reversal."
              marginY={2}
            />
          </Box>
          <ComposeDecisionEmail
            organizationId={organizationId}
            submissionContextId={submissionContextId}
            commandName="RevertDecision"
            formPayload={methods.getValues()}
          />
          <EditorialNote value={editorNote} onChange={setEditorNote} />
        </form>
      </FormProvider>
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="caution"
              onClick={() => methods.handleSubmit(onSubmitFirstStep)()}
              isLoading={
                !hasCustomizableTemplates && revertDecisionMutation.isPending
              }
            >
              {hasCustomizableTemplates ? 'Continue' : 'Revert Decision'}
            </Button>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() =>
              emailContentMethods.handleSubmit(handleConfirmDecision)()
            }
            variant="caution"
            isLoading={revertDecisionMutation.isPending}
          >
            Revert Decision
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="primarySecondaryFill">
        <DecisionLayout manuscriptId={manuscriptId} onClose={onClose}>
          {currentStep === 1
            ? renderFirstStep()
            : hasCustomizableTemplates
            ? renderEmailStep()
            : null}
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
